import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, Check, MessageCircle, CreditCard, FileText, ChevronRight, ChevronLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import logo from "@/assets/logo.png";
import { PHOTOS } from "@/assets/photos";
import extractErrorMessage from "@/utils/extractErrorMessage";
import { useActivePromotion } from "@/hooks/useActivePromotion";
import { getBackendUrl } from "@/utils/apiBase";
import { trackFbEvent, trackGaEvent } from "@/utils/tracking";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/* ——— Fallback packages/options (used if backend is unreachable) ——— */
const DEFAULT_PACKAGES = {
  "3h": { name: "Forfait 3 heures", price: 270, duration: "3h" },
  "5h": { name: "Forfait 5 heures", price: 340, duration: "5h" },
  "7h": { name: "Forfait 7 heures", price: 400, duration: "7h" },
};
const DEFAULT_OPTIONS = { magnet_50: 50, magnet_100: 80, backdrop: 40 };

/* ——— Font Loader ——— */
const useFonts = () => {
  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Cormorant+Garamond:ital,wght@1,400&family=DM+Sans:wght@400;500;600&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);
};

/* ——— Navbar (used only when not embedded) ——— */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-md" : "bg-transparent"}`}
      data-testid="booking-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img
            src={logo}
            alt="SelfieBooth Event"
            style={{ height: "48px", width: "auto", filter: scrolled ? "none" : "brightness(0) invert(1)", transition: "filter 0.35s ease" }}
            data-testid="booking-nav-logo"
          />
        </Link>
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: "Accueil", href: "/" },
            { label: "Galerie", href: "/galerie" },
            { label: "Réserver", href: "/booking" },
          ].map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className={`font-['DM_Sans'] text-sm font-medium ${scrolled ? "text-[#1a1a1a]" : "text-white"} hover:text-[#b8977e] transition-colors`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/landing"
            className="font-['DM_Sans'] text-sm font-semibold bg-[#b8977e] text-white px-6 py-2.5 rounded-full hover:bg-[#a68770] transition-colors"
          >
            Devis gratuit
          </Link>
        </div>
      </div>
    </nav>
  );
};

/* ——— Step Indicator ——— */
const StepIndicator = ({ currentStep }) => {
  const steps = [
    { num: 1, label: "Forfait & Date" },
    { num: 2, label: "Informations" },
    { num: 3, label: "Signature du devis" },
  ];

  return (
    <div style={{ background: "#fff", padding: "1.5rem 0", borderBottom: "1px solid #f0e4da" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 1rem" }} data-testid="step-indicator">
        {steps.map((s, i) => {
          const isPast = currentStep > s.num;
          const isActive = currentStep === s.num;
          const isFuture = currentStep < s.num;
          return (
            <div key={s.num} style={{ display: "flex", alignItems: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.3rem" }}>
                <div
                  style={{
                    width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                    background: isPast || isActive ? "#b8977e" : "#fff",
                    border: isFuture ? "2px solid #ddd" : "2px solid #b8977e",
                    transition: "all 0.4s ease",
                  }}
                >
                  {isPast ? (
                    <Check style={{ width: 16, height: 16, color: "#fff", strokeWidth: 3 }} />
                  ) : (
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.85rem", color: isActive ? "#fff" : "#ccc" }}>{s.num}</span>
                  )}
                </div>
                <span
                  style={{
                    fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#b8977e" : isPast ? "#777" : "#bbb",
                    transition: "all 0.4s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div
                  style={{
                    width: 42, height: 2, margin: "0 0.45rem", marginBottom: "1.2rem",
                    background: currentStep > s.num ? "#b8977e" : "#ddd",
                    transition: "background 0.4s ease",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ——— Input Styles ——— */
const inputStyle = {
  border: "1.5px solid #e8d5c4", borderRadius: "0.75rem", padding: "0.85rem 1rem",
  fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem", width: "100%", outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
  background: "#fff",
};
const inputFocusClass = "focus:border-[#b8977e] focus:shadow-[0_0_0_3px_rgba(184,151,126,0.15)]";

/* ——— Availability Calendar ——— */
const MONTH_NAMES_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DAY_NAMES_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const AvailabilityCalendar = ({ bookedDates, selectedDate, onSelectDate, loading, loadError, onRetry }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const dateMap = useMemo(() => {
    const m = {};
    (bookedDates || []).forEach(d => { m[d.date] = d; });
    return m;
  }, [bookedDates]);

  const calendarDays = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0).getDate();
    let startDow = first.getDay() - 1;
    if (startDow < 0) startDow = 6;
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= lastDay; d++) days.push(d);
    return days;
  }, [viewYear, viewMonth]);

  const goMonth = (dir) => {
    let m = viewMonth + dir;
    let y = viewYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setViewMonth(m);
    setViewYear(y);
  };

  const canGoPrev = viewYear > today.getFullYear() || (viewYear === today.getFullYear() && viewMonth > today.getMonth());
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div style={{ background: "#f5ede8", borderRadius: "1.5rem", padding: "1.5rem 1.5rem 1rem", position: "relative" }} data-testid="availability-calendar">
      <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a", fontSize: "0.95rem", marginBottom: "1rem", textAlign: "center" }}>
        Choisissez votre date d'événement
      </p>

      {loadError && (
        <div
          data-testid="availability-error"
          style={{ background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "0.75rem", padding: "0.75rem 1rem", marginBottom: "1rem", color: "#991b1b", fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem" }}
        >
          <span>⚠️ Impossible de charger les disponibilités</span>
          {onRetry && (
            <button onClick={onRetry} type="button" style={{ background: "transparent", border: "1.5px solid #991b1b", color: "#991b1b", fontWeight: 600, padding: "0.3rem 0.75rem", borderRadius: 50, fontSize: "0.78rem", cursor: "pointer" }} data-testid="availability-retry">
              Réessayer
            </button>
          )}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
        <button
          onClick={() => goMonth(-1)}
          disabled={!canGoPrev}
          style={{ width: 36, height: 36, borderRadius: "50%", border: "1.5px solid #e8d5c4", background: "#fff", cursor: canGoPrev ? "pointer" : "not-allowed", opacity: canGoPrev ? 1 : 0.3, display: "flex", alignItems: "center", justifyContent: "center" }}
          data-testid="cal-prev-month"
        >
          <ChevronLeft style={{ width: 18, height: 18, color: "#b8977e" }} />
        </button>
        <span style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "1.15rem", color: "#1a1a1a" }}>
          {MONTH_NAMES_FR[viewMonth]} {viewYear}
        </span>
        <button
          onClick={() => goMonth(1)}
          style={{ width: 36, height: 36, borderRadius: "50%", border: "1.5px solid #e8d5c4", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          data-testid="cal-next-month"
        >
          <ChevronRight style={{ width: 18, height: 18, color: "#b8977e" }} />
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
        {DAY_NAMES_FR.map(d => (
          <div key={d} style={{ textAlign: "center", fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", fontWeight: 600, color: "#999", padding: "0.3rem 0" }}>
            {d}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, position: "relative" }}>
        {loading && (
          <div
            data-testid="availability-loading"
            style={{ position: "absolute", inset: 0, background: "rgba(245,237,232,0.78)", borderRadius: "0.75rem", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5, backdropFilter: "blur(2px)" }}
          >
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: "#b8977e", fontWeight: 600 }}>
              Chargement des disponibilités…
            </span>
          </div>
        )}
        {calendarDays.map((day, i) => {
          if (day === null) return <div key={`e-${i}`} />;
          const dateStr = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`;
          const cellDate = new Date(viewYear, viewMonth, day);
          const isPast = cellDate < today;
          const isSelected = selectedDate === dateStr;
          const info = dateMap[dateStr];
          const count = info ? info.count : 0;
          const isFull = count >= 2;
          const isPartial = count === 1;
          const isToday = cellDate.getTime() === today.getTime();

          let bg = "#fff";
          let color = "#1a1a1a";
          let border = "2px solid transparent";
          let cursor = "pointer";
          let opacity = 1;
          let fontWeight = 400;

          if (isPast) {
            bg = "#f0eded"; color = "#bbb"; cursor = "not-allowed"; opacity = 0.6;
          } else if (isFull) {
            bg = "#fee2e2"; color = "#991b1b"; cursor = "not-allowed";
          } else if (isPartial) {
            bg = "#fff7ed"; color = "#c2410c";
          } else {
            bg = "#ecfdf5"; color = "#065f46";
          }

          if (isSelected && !isPast && !isFull) {
            border = "2px solid #b8977e";
            bg = "#fef9f5";
            fontWeight = 700;
            color = "#b8977e";
          }

          if (isToday && !isSelected) {
            fontWeight = 700;
          }

          return (
            <button
              key={dateStr}
              onClick={() => {
                if (isPast) return;
                if (isFull) { toast.error("Date complète (2/2 photobooths)"); return; }
                onSelectDate(dateStr, count);
              }}
              style={{
                width: "100%", aspectRatio: "1", borderRadius: "0.6rem", border, background: bg,
                cursor, opacity, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight, color,
                transition: "all 0.15s ease", position: "relative", padding: 0,
              }}
              data-testid={`cal-day-${dateStr}`}
            >
              {day}
              {!isPast && (isPartial || isFull) && (
                <span style={{ fontSize: "0.55rem", fontWeight: 600, marginTop: 1, lineHeight: 1 }}>
                  {count}/2
                </span>
              )}
              {isToday && (
                <span style={{ position: "absolute", bottom: 2, left: "50%", transform: "translateX(-50%)", width: 4, height: 4, borderRadius: "50%", background: isSelected ? "#b8977e" : "#1a1a1a" }} />
              )}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: "1.2rem", marginTop: "1rem", flexWrap: "wrap" }}>
        {[
          { bg: "#ecfdf5", border: "#bbf7d0", label: "Disponible" },
          { bg: "#fff7ed", border: "#fed7aa", label: "1 restant" },
          { bg: "#fee2e2", border: "#fecaca", label: "Complet" },
        ].map(l => (
          <div key={l.label} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: l.bg, border: `1.5px solid ${l.border}`, display: "inline-block" }} />
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", color: "#777" }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ——— Main Wizard Component ———
 * Props:
 *  - embedded (bool, default false): if true, hide Navbar + Hero + fixed mobile bar (used inside LandingPage)
 *  - onSuccess (optional fn): callback after successful devis submit
 */
/* ——— Pricing Type Selector (Step 0 — choose Illimité vs Par impressions) ——— */
const PricingTypeSelector = ({ onSelect }) => {
  return (
    <section
      data-testid="pricing-type-selector"
      style={{ minHeight: "70vh", background: "#f5ede8", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "4rem 1.5rem" }}
    >
      <div style={{ textAlign: "center", maxWidth: 680, marginBottom: "3rem" }}>
        <span data-testid="pricing-selector-step-badge" style={{ display: "inline-block", background: "#b8977e", color: "#fff", fontSize: "0.7rem", fontWeight: 600, padding: "0.35rem 1rem", borderRadius: 50, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem", fontFamily: "'DM Sans',sans-serif" }}>
          Étape 1 sur 4
        </span>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(2rem, 4vw, 2.5rem)", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.15, marginBottom: "0.75rem" }}>
          Quel format vous correspond ?
        </h1>
        <p style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic", fontSize: "1.2rem", color: "#7a6a5e" }}>
          Choisissez votre formule pour commencer
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "2rem", maxWidth: 1100, width: "100%" }}>
        {[
          {
            id: "unlimited",
            icon: "🎉",
            title: "ILLIMITÉ",
            tagline: "Impressions illimitées pendant la durée choisie",
            bullets: ["Tirages sans limite", "3h / 5h / 7h au choix", "Idéal grands événements"],
            from: 270,
            testid: "select-unlimited",
          },
          {
            id: "prints",
            icon: "📸",
            title: "PAR IMPRESSIONS",
            tagline: "Nombre de tirages défini avec durée incluse",
            bullets: ["100 / 300 / 700 impressions", "Budget maîtrisé", "Idéal selon nombre d'invités"],
            from: 190,
            testid: "select-prints",
          },
        ].map((card) => (
          <button
            key={card.id}
            type="button"
            data-testid={card.testid}
            onClick={() => onSelect(card.id)}
            style={{
              background: "#fff",
              border: "1.5px solid #f0e4da",
              borderRadius: "1.5rem",
              padding: "2.5rem 2rem",
              textAlign: "left",
              cursor: "pointer",
              transition: "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
              fontFamily: "'DM Sans',sans-serif",
              boxShadow: "0 2px 14px rgba(0,0,0,0.04)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-6px)";
              e.currentTarget.style.boxShadow = "0 16px 40px rgba(184,151,126,0.22)";
              e.currentTarget.style.borderColor = "#b8977e";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 14px rgba(0,0,0,0.04)";
              e.currentTarget.style.borderColor = "#f0e4da";
            }}
          >
            <div style={{ fontSize: "3rem", marginBottom: "0.75rem", lineHeight: 1 }}>{card.icon}</div>
            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "2rem", fontWeight: 700, color: "#1a1a1a", letterSpacing: "0.02em", marginBottom: "0.5rem" }}>
              {card.title}
            </h3>
            <p style={{ color: "#7a6a5e", fontSize: "0.95rem", marginBottom: "1.25rem", lineHeight: 1.5 }}>
              {card.tagline}
            </p>
            <ul style={{ listStyle: "none", padding: 0, marginBottom: "1.5rem" }}>
              {card.bullets.map((b, i) => (
                <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.88rem", color: "#444", marginBottom: "0.4rem" }}>
                  <Check style={{ width: 14, height: 14, color: "#b8977e", flexShrink: 0 }} />
                  {b}
                </li>
              ))}
            </ul>
            <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.4rem", fontWeight: 700, color: "#b8977e", marginBottom: "1rem" }}>
              À partir de {card.from}€
            </p>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", color: "#b8977e", fontWeight: 600, fontSize: "0.95rem" }}>
              Choisir cette formule
              <ChevronRight style={{ width: 16, height: 16 }} />
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};

export const BookingWizard = ({ embedded = false, onSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useFonts();

  const [packages, setPackages] = useState({});
  const [optionsPrices, setOptionsPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const { promo: activePromotion, getDiscountedPrice } = useActivePromotion();
  const [currentStep, setCurrentStep] = useState(1);
  const [slideDir, setSlideDir] = useState("right");
  const [animKey, setAnimKey] = useState(0);
  // Phase 1 — pricing type selector ("unlimited" / "prints"). null = show selector.
  // Nov 2026 pivot: PricingTypeSelector is hidden client-side (feature disabled).
  // Force "unlimited" to skip the selector and land directly on step 1 (Forfait+Date).
  const [pricingType, setPricingType] = useState("unlimited");
  // Flag: hide the "← Changer de formule" button when the selector is disabled.
  const PRICING_SELECTOR_ENABLED = false;

  const [selectedPackage, setSelectedPackage] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [dateStatus, setDateStatus] = useState(null);
  const [bookedCount, setBookedCount] = useState(0);
  const [bookedDates, setBookedDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(true);
  const [datesError, setDatesError] = useState(false);
  const [eventStartTime, setEventStartTime] = useState("");
  const [selectedOptions, setSelectedOptions] = useState({ magnet: false, backdrop: false });
  const [magnetSize, setMagnetSize] = useState("10x15");
  const [magnetColor, setMagnetColor] = useState("blanc");
  const [magnetQty, setMagnetQty] = useState("50");
  const [showMagnetInfo, setShowMagnetInfo] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [deliveryZone, setDeliveryZone] = useState("idf");
  const [message, setMessage] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("devis");
  const [quoteAccepted, setQuoteAccepted] = useState(false);

  useEffect(() => {
    fetchPackages();
    fetchBookedDates();
    const forfait = searchParams.get("forfait");
    if (forfait && ["3h", "5h", "7h"].includes(forfait)) {
      setSelectedPackage(forfait);
      setPricingType("unlimited"); // legacy URL → assume unlimited
    }
    // Allow ?formule=unlimited|prints to deep-link the selector
    const formule = searchParams.get("formule");
    if (formule === "unlimited" || formule === "prints") {
      setPricingType(formule);
    }
  }, []);

  useEffect(() => {
    if (!showMagnetInfo) return;
    const onKey = (e) => { if (e.key === "Escape") setShowMagnetInfo(false); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [showMagnetInfo]);

  const fetchPackages = async () => {
    try {
      const r = await axios.get(`${API}/packages`);
      const pkgs = r.data?.packages;
      const opts = r.data?.options;
      if (pkgs && Object.keys(pkgs).length > 0) {
        setPackages(pkgs);
        setOptionsPrices(opts || DEFAULT_OPTIONS);
      } else {
        // Backend replied but payload was empty — fall back silently
        setPackages(DEFAULT_PACKAGES);
        setOptionsPrices(DEFAULT_OPTIONS);
      }
    } catch (e) {
      // Silent fallback: log for debugging, but never disrupt the user flow.
      console.warn("[BookingWizard] /api/packages unreachable — using defaults", e?.message);
      setPackages(DEFAULT_PACKAGES);
      setOptionsPrices(DEFAULT_OPTIONS);
    }
  };

  const fetchActivePromotion = async () => {
    try {
      const r = await axios.get(`${API}/promotions/active`);
      if (r.data) setActivePromotion(r.data);
    } catch (e) { /* silent */ }
  };

  // Re-fetch availability when entering the date-selection step (defense in depth)
  useEffect(() => {
    if (pricingType !== null && currentStep === 1) {
      fetchBookedDates();
    }
  }, [pricingType, currentStep]);

  const fetchBookedDates = async () => {
    setDatesLoading(true);
    setDatesError(false);
    try {
      const r = await axios.get(`${API}/availability/booked-dates`);
      setBookedDates(r.data.booked_dates || []);
    } catch (e) {
      // Surface the failure — NEVER show "all available" silently.
      console.error("[BookingWizard] fetchBookedDates failed", e?.message);
      setDatesError(true);
      setBookedDates([]);
    } finally {
      setDatesLoading(false);
    }
  };

  const calculatePrice = (originalPrice) => {
    if (!activePromotion || !activePromotion.discount_value) return { original: originalPrice, final: originalPrice, hasDiscount: false };
    let discount = activePromotion.discount_type === "percentage" ? (originalPrice * activePromotion.discount_value) / 100 : activePromotion.discount_value;
    return { original: originalPrice, final: originalPrice - discount, hasDiscount: true, discount };
  };

  const calculateTotal = () => {
    if (!selectedPackage || !packages[selectedPackage]) return 0;
    const pkgPrice = packages[selectedPackage].price;
    let optsTotal = 0;
    if (selectedOptions.magnet) {
      optsTotal += magnetQty === "100" ? 80 : 50;
    }
    if (selectedOptions.backdrop) optsTotal += (optionsPrices.backdrop || 40);
    const deliverySurcharge = deliveryZone === "frontalier" ? 30 : 0;
    return calculatePrice(pkgPrice).final + optsTotal + deliverySurcharge;
  };

  const checkDate = async (dateStr) => {
    setEventDate(dateStr);
    setDateStatus(null);
    setBookedCount(0);
    if (!dateStr) return;
    // Client-side fallback from already-loaded bookedDates (used if API is down)
    const local = (bookedDates || []).find((d) => d.date === dateStr);
    const localCount = local ? local.count : 0;
    try {
      const r = await axios.post(`${API}/availability/check`, { event_date: dateStr });
      setBookedCount(r.data.booked_count || 0);
      setDateStatus(r.data.available ? "available" : "unavailable");
      if (!r.data.available) toast.error("Date complète (2/2 photobooths)");
    } catch (e) {
      // Resilient path: infer availability from the booked-dates payload we
      // already have, so the user is NEVER blocked on "Continuer" because of
      // a transient network error on /availability/check.
      console.warn("[BookingWizard] /availability/check unreachable — using cached booked-dates", e?.message);
      setBookedCount(localCount);
      setDateStatus(localCount >= 2 ? "unavailable" : "available");
    }
  };

  const goStep = (step) => {
    setSlideDir(step > currentStep ? "right" : "left");
    setAnimKey((k) => k + 1);
    // Lead event when the client transitions from infos (step 2) → signature (step 3)
    if (currentStep === 2 && step === 3) {
      trackFbEvent("Lead", { content_category: "booking" });
      trackGaEvent("generate_lead", { event_category: "booking" });
    }
    setCurrentStep(step);
    // Scroll: in embedded mode scroll to wizard root, else top
    if (embedded) {
      const el = document.getElementById("booking-wizard-root");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ViewContent event when the client lands on the wizard (step 1 = Forfait & Date)
  useEffect(() => {
    if (currentStep === 1 && pricingType !== null) {
      trackFbEvent("ViewContent", { content_type: "package", content_category: "booking" });
    }
  }, []);

  const customerName = `${firstName} ${lastName}`.trim();
  const canStep2 = eventDate && dateStatus === "available" && selectedPackage && selectedPackage !== "contact";
  const canStep3 = firstName && lastName && customerEmail && customerPhone && eventLocation && deliveryZone !== "hors_zone";

  const buildOptionsPayload = () => {
    const opts = {};
    if (selectedOptions.magnet) {
      opts[magnetQty === "100" ? "magnet_100" : "magnet_50"] = true;
      opts.magnet_size = magnetSize;
      opts.magnet_color = magnetColor;
    }
    if (selectedOptions.backdrop) opts.backdrop = true;
    return opts;
  };

  const handleSubmit = async () => {
    if (!canStep3) { toast.error("Remplissez tous les champs obligatoires"); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) { toast.error("Email invalide"); return; }
    if (!quoteAccepted) { toast.error("Vous devez accepter le devis pour r\u00e9server"); return; }

    setLoading(true);
    try {
      let promotionData = null;
      if (activePromotion?.discount_value) {
        promotionData = { title: activePromotion.title, type: activePromotion.discount_type, value: activePromotion.discount_value };
      }

      // Nov 2026 pivot: client tunnel is always a signed devis (no Stripe).
      const response = await axios.post(`${API}/bookings`, {
        package_id: selectedPackage,
        event_date: eventDate,
        event_start_time: eventStartTime,
        options: buildOptionsPayload(),
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        event_type: eventType,
        event_location: eventLocation,
        delivery_zone: deliveryZone,
        payment_method: "devis",
        promotion: promotionData,
        pricing_type: pricingType,
        quote_accepted: true,
      });

      toast.success("R\u00e9servation confirm\u00e9e !");
      const bookingResp = response?.data || {};
      // Meta Pixel + GA conversion events (only fire if consent granted → fbq/gtag exist)
      trackFbEvent("CompleteRegistration");
      trackFbEvent("Purchase", {
        value: bookingResp.total_price ?? total,
        currency: "EUR",
        content_ids: [selectedPackage],
        content_type: "product",
      });
      trackGaEvent("purchase", {
        transaction_id: bookingResp.id,
        value: bookingResp.total_price ?? total,
        currency: "EUR",
        items: [{ item_id: selectedPackage, item_name: packages[selectedPackage]?.name }],
      });
      if (onSuccess) onSuccess();
      navigate("/merci", {
        state: {
          booking: bookingResp,
          firstName,
          lastName,
          total: response?.data?.total_price ?? total,
          eventDate,
          signedQuote: true,
        },
      });
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erreur lors de la r\u00e9servation"));
    } finally { setLoading(false); }
  };

  const total = calculateTotal();
  const pkgInfo = selectedPackage && packages[selectedPackage] ? packages[selectedPackage] : null;
  const pkgPriceInfo = pkgInfo ? calculatePrice(pkgInfo.price) : null;
  const optLabels = { magnet: `Photo aimantée ${magnetSize} ${magnetColor === "blanc" ? "Blanc" : "Noir"} (${magnetQty} unités)`, backdrop: "Fond pour Photos" };

  const slideStyle = {
    animation: `slideIn${slideDir === "right" ? "R" : "L"} 0.4s ease`,
  };

  return (
    <div id="booking-wizard-root" className={embedded ? "font-['DM_Sans']" : "min-h-screen bg-white font-['DM_Sans']"} data-testid={embedded ? "booking-wizard-embedded" : "booking-page"}>
      <style>{`
        @keyframes slideInR { from { opacity:0; transform:translateX(30px); } to { opacity:1; transform:translateX(0); } }
        @keyframes slideInL { from { opacity:0; transform:translateX(-30px); } to { opacity:1; transform:translateX(0); } }
        @keyframes checkDraw { from { stroke-dashoffset:48; } to { stroke-dashoffset:0; } }
        @keyframes magnetRecapFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes magnetModalFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes magnetModalScale { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      {!embedded && <Navbar />}

      {!embedded && (
        <section className="relative" style={{ height: "45vh", minHeight: 320 }} data-testid="booking-hero">
          <img src={PHOTOS.guests} alt="Photobooth event" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(0,0,0,0.65), rgba(0,0,0,0.35))" }} />
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 pt-16">
            <span className="bg-[#b8977e]/90 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wider font-['DM_Sans']">Réservation en ligne</span>
            <h1 className="font-['Playfair_Display'] font-bold text-white leading-tight mb-3" style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>
              Réservez votre Photobooth
            </h1>
            <p className="font-['DM_Sans'] text-white/85 text-base max-w-lg">
              Choisissez votre forfait et payez en ligne en toute sécurité
            </p>
          </div>
        </section>
      )}

      {/* Step 0 — Pricing type selector */}
      {PRICING_SELECTOR_ENABLED && pricingType === null ? (
        <PricingTypeSelector
          onSelect={(t) => {
            setPricingType(t);
            // Reset any preselected package — different categories
            setSelectedPackage("");
          }}
        />
      ) : (
        <>
          <StepIndicator currentStep={currentStep} />
          {/* "Change formule" button — hidden while the selector is disabled */}
          {PRICING_SELECTOR_ENABLED && (
            <div style={{ maxWidth: 750, margin: "1rem auto 0", padding: "0 1.5rem" }}>
              <button
                type="button"
                data-testid="back-to-pricing-type"
                onClick={() => { setPricingType(null); setSelectedPackage(""); setCurrentStep(1); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.4rem",
                  background: "transparent", border: "none", color: "#b8977e",
                  fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600,
                  cursor: "pointer", padding: "0.25rem 0",
                }}
              >
                <ChevronLeft style={{ width: 14, height: 14 }} />
                Changer de formule
                <span style={{ marginLeft: "0.4rem", background: "#fff9f7", color: "#b8977e", fontSize: "0.72rem", padding: "0.15rem 0.6rem", borderRadius: 50, border: "1px solid #f0e4da" }}>
                  {pricingType === "prints" ? "📸 Par impressions" : "🎉 Illimité"}
                </span>
              </button>
            </div>
          )}
        </>
      )}

      {pricingType !== null && (
      <div className={embedded ? "pb-12" : "pb-32 md:pb-12"}>
        {/* ——— STEP 1 ——— */}
        {currentStep === 1 && (
          <div key={`s1-${animKey}`} style={slideStyle} className="py-8">
            <div style={{ maxWidth: 750, margin: "0 auto 2rem", padding: "0 1.5rem" }}>
              <AvailabilityCalendar
                bookedDates={bookedDates}
                selectedDate={eventDate}
                onSelectDate={(dateStr) => { checkDate(dateStr); }}
                loading={datesLoading}
                loadError={datesError}
                onRetry={fetchBookedDates}
              />
              {dateStatus === "available" && (
                <div style={{ marginTop: "0.75rem", background: "#ecfdf5", color: "#065f46", padding: "0.6rem 1rem", borderRadius: "0.5rem", fontSize: "0.85rem", fontWeight: 500, textAlign: "center" }} data-testid="date-available">
                  {bookedCount === 0 ? "Date disponible — Réservez maintenant !" : `Date disponible — ${bookedCount}/2 photobooth déjà réservé`}
                </div>
              )}
              {dateStatus === "unavailable" && (
                <div style={{ marginTop: "0.75rem", background: "#fef2f2", color: "#991b1b", padding: "0.6rem 1rem", borderRadius: "0.5rem", fontSize: "0.85rem", fontWeight: 500, textAlign: "center" }} data-testid="date-unavailable">
                  Date complète (2/2 photobooths) — choisissez une autre date
                </div>
              )}
            </div>

            <div style={{ maxWidth: 750, margin: "0 auto 2rem", padding: "0 1.5rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1.2rem" }}>
                {Object.entries(packages).filter(([_, pkg]) => (pkg.pricing_type || "unlimited") === pricingType).map(([key, pkg]) => {
                  const sel = selectedPackage === key;
                  const priceInfo = calculatePrice(pkg.price);
                  const isPopular = key === "5h" || key === "confort_print";
                  const isPrints = (pkg.pricing_type || "unlimited") === "prints";
                  const maxPrints = pkg.max_prints || 0;
                  return (
                    <div
                      key={key}
                      onClick={() => setSelectedPackage(sel ? "" : key)}
                      style={{
                        position: "relative", border: sel ? "2px solid #b8977e" : "1.5px solid #f0e4da",
                        borderRadius: "1.5rem", padding: "2rem", background: sel ? "#fff9f7" : "#fff",
                        cursor: "pointer", transition: "all 0.25s ease",
                        boxShadow: sel ? "0 6px 25px rgba(184,151,126,0.2)" : "none",
                      }}
                      data-testid={`package-card-${key}`}
                    >
                      {isPopular && (
                        <div style={{ position: "absolute", top: "-0.9rem", left: "50%", transform: "translateX(-50%)", background: "#1a1a1a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", fontWeight: 600, borderRadius: 50, padding: "0.35rem 1.1rem", whiteSpace: "nowrap" }}>
                          Populaire
                        </div>
                      )}
                      {isPrints && (
                        <div
                          data-testid={`package-prints-badge-${key}`}
                          style={{ position: "absolute", top: "0.75rem", right: "0.75rem", background: "#fff9f7", color: "#b8977e", border: "1px solid #f0e4da", fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", fontWeight: 700, padding: "0.3rem 0.7rem", borderRadius: 50, whiteSpace: "nowrap" }}
                        >
                          📸 {maxPrints} impressions
                        </div>
                      )}
                      {sel && (
                        <div style={{ position: "absolute", top: "0.75rem", right: isPrints ? "auto" : "0.75rem", left: isPrints ? "0.75rem" : "auto", width: 28, height: 28, borderRadius: "50%", background: "#b8977e", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Check style={{ width: 14, height: 14, color: "#fff", strokeWidth: 3 }} />
                        </div>
                      )}
                      <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.4rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.2rem", marginTop: isPrints ? "1.6rem" : 0 }}>{pkg.name}</p>
                      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#b8977e", marginBottom: "0.5rem" }}>{pkg.duration}</p>
                      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#777", marginBottom: "1rem" }}>{pkg.description || "Location de photobooth professionnel"}</p>
                      {priceInfo.hasDiscount && (
                        <p style={{ fontFamily: "'DM Sans',sans-serif", color: "#bbb", textDecoration: "line-through", fontSize: "0.9rem" }}>{priceInfo.original}€</p>
                      )}
                      <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "2rem", fontWeight: 700, color: "#b8977e" }}>{priceInfo.final.toFixed(0)}€</p>
                      {isPrints && maxPrints > 0 && (
                        <div data-testid={`package-prints-gauge-${key}`} style={{ marginTop: "0.5rem", marginBottom: "0.25rem" }}>
                          <div style={{ width: "100%", height: 6, background: "#f0e4da", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ width: `${Math.min(100, (maxPrints / 700) * 100)}%`, height: "100%", background: "linear-gradient(90deg, #b8977e, #d4b39c)", borderRadius: 4 }} />
                          </div>
                          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", color: "#999", marginTop: "0.25rem" }}>
                            {maxPrints} / 700 impressions
                          </p>
                        </div>
                      )}
                      <div style={{ borderTop: "1px solid #f0e4da", margin: "1rem 0", paddingTop: "1rem" }}>
                        {(pkg.features || ["Photobooth autonome", "1 imprimante incluse", "Impressions illimitées qualité photo", "Accessoires & props inclus", "Galerie digitale offerte", "Livraison & installation IDF incluses", "Prise en main à distance"]).map((f, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.35rem" }}>
                            <Check style={{ width: 14, height: 14, color: "#b8977e", flexShrink: 0 }} />
                            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#555" }}>{f}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                onClick={() => setSelectedPackage(selectedPackage === "contact" ? "" : "contact")}
                style={{
                  marginTop: "1.2rem", border: selectedPackage === "contact" ? "2px solid #b8977e" : "1.5px solid #f0e4da",
                  borderRadius: "1.5rem", padding: "1.2rem 2rem", background: selectedPackage === "contact" ? "#fff9f7" : "#fff",
                  cursor: "pointer", textAlign: "center", transition: "all 0.25s ease",
                }}
                data-testid="package-card-contact"
              >
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a" }}>Demandes spécifiques / Nous contacter</p>
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#777" }}>Pour toute demande particulière</p>
              </div>
            </div>

            {selectedPackage === "contact" && (
              <div style={{ maxWidth: 750, margin: "0 auto 2rem", padding: "0 1.5rem", textAlign: "center" }}>
                <div style={{ background: "#fff", border: "1.5px solid #f0e4da", borderRadius: "1.5rem", padding: "3rem 2rem" }}>
                  <MessageCircle style={{ width: 48, height: 48, color: "#25D366", margin: "0 auto 1rem" }} />
                  <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.4rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.5rem" }}>Contactez-nous</h3>
                  <p style={{ fontFamily: "'DM Sans',sans-serif", color: "#777", marginBottom: "1.5rem" }}>Notre équipe est à votre disposition</p>
                  <a
                    href="https://wa.me/33679393429?text=Bonjour, je souhaiterais faire une demande sp%C3%A9cifique pour la location d'un photobooth."
                    target="_blank" rel="noopener noreferrer"
                    style={{ display: "inline-block", background: "#25D366", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, padding: "0.9rem 2rem", borderRadius: 50, textDecoration: "none" }}
                    data-testid="contact-whatsapp-btn"
                  >
                    Nous contacter via WhatsApp
                  </a>
                </div>
              </div>
            )}

            {selectedPackage && selectedPackage !== "contact" && (
              <div style={{ maxWidth: 750, margin: "0 auto 2rem", padding: "0 1.5rem", opacity: 1, transition: "opacity 0.3s" }}>
                <div style={{ background: "#fff", border: "1.5px solid #f0e4da", borderRadius: "1.5rem", padding: "2rem" }}>
                  <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a", marginBottom: "1.2rem" }}>Personnalisez votre expérience</h3>

                  <div style={{ border: selectedOptions.magnet ? "2px solid #b8977e" : "1.5px solid #f0e4da", borderRadius: "1rem", padding: "1.2rem", marginBottom: "0.75rem", background: selectedOptions.magnet ? "#fff9f7" : "#fff", transition: "all 0.2s" }} data-testid="option-magnet">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setSelectedOptions(o => ({ ...o, magnet: !o.magnet }))}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <input type="checkbox" checked={selectedOptions.magnet} readOnly style={{ width: 20, height: 20, accentColor: "#b8977e" }} />
                        <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a", fontSize: "0.95rem" }}>Photo aimantée</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setShowMagnetInfo(true); }}
                          aria-label="Voir un exemple de photo aimantée"
                          data-testid="magnet-info-help-btn"
                          style={{
                            width: 20, height: 20, borderRadius: "50%",
                            border: "1px solid #b8977e", color: "#b8977e",
                            background: "#fff", cursor: "pointer",
                            fontSize: "12px", fontWeight: 700, lineHeight: 1,
                            fontFamily: "'DM Sans',sans-serif",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            padding: 0,
                          }}
                        >
                          ?
                        </button>
                      </div>
                      <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, color: "#b8977e" }}>+{magnetQty === "100" ? "80" : "50"}€</span>
                    </div>

                    {selectedOptions.magnet && (
                      <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #f0e4da" }}>
                        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", fontWeight: 600, color: "#777", marginBottom: "0.5rem" }}>1. Taille</p>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "1rem" }}>
                          {[
                            { id: "10x15", label: "10×15 cm", sub: "Format classique" },
                            { id: "5x15", label: "5×15 cm", sub: "Format bandelette" },
                          ].map(s => (
                            <button key={s.id} onClick={() => setMagnetSize(s.id)} style={{ padding: "0.6rem", borderRadius: "0.5rem", border: magnetSize === s.id ? "2px solid #b8977e" : "1.5px solid #e8d5c4", background: magnetSize === s.id ? "#fef9f5" : "#fff", cursor: "pointer", textAlign: "center" }} data-testid={`magnet-size-${s.id}`}>
                              <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.85rem", color: "#1a1a1a" }}>{s.label}</p>
                              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", color: "#999" }}>{s.sub}</p>
                            </button>
                          ))}
                        </div>

                        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", fontWeight: 600, color: "#777", marginBottom: "0.5rem" }}>2. Coloris</p>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "1rem" }}>
                          {[
                            { id: "blanc", label: "Blanc", swatch: "#fff", border: "#ddd" },
                            { id: "noir", label: "Noir", swatch: "#1a1a1a", border: "#1a1a1a" },
                          ].map(c => (
                            <button key={c.id} onClick={() => setMagnetColor(c.id)} style={{ padding: "0.6rem", borderRadius: "0.5rem", border: magnetColor === c.id ? "2px solid #b8977e" : "1.5px solid #e8d5c4", background: magnetColor === c.id ? "#fef9f5" : "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }} data-testid={`magnet-color-${c.id}`}>
                              <span style={{ width: 20, height: 20, borderRadius: "50%", background: c.swatch, border: `2px solid ${c.border}`, display: "inline-block" }} />
                              <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.85rem", color: "#1a1a1a" }}>{c.label}</span>
                            </button>
                          ))}
                        </div>

                        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", fontWeight: 600, color: "#777", marginBottom: "0.5rem" }}>3. Quantité</p>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "1rem" }}>
                          {[
                            { id: "50", label: "50 unités", price: "+50€" },
                            { id: "100", label: "100 unités", price: "+80€" },
                          ].map(q => (
                            <button key={q.id} onClick={() => setMagnetQty(q.id)} style={{ padding: "0.6rem", borderRadius: "0.5rem", border: magnetQty === q.id ? "2px solid #b8977e" : "1.5px solid #e8d5c4", background: magnetQty === q.id ? "#fef9f5" : "#fff", cursor: "pointer", textAlign: "center" }} data-testid={`magnet-qty-${q.id}`}>
                              <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.85rem", color: "#1a1a1a" }}>{q.label}</p>
                              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", fontWeight: 700, color: "#b8977e" }}>{q.price}</p>
                            </button>
                          ))}
                        </div>

                        <div
                          style={{
                            background: "#faf8f5", border: "1px solid #b8977e", borderRadius: "12px",
                            padding: "16px", display: "flex", flexDirection: "column",
                            alignItems: "center", justifyContent: "center", gap: "12px",
                          }}
                          data-testid="magnet-preview-card"
                        >
                          <img
                            src={`/images/magnets/${magnetSize}_${magnetColor}.jpg`}
                            alt={`Photo aimantée ${magnetSize} ${magnetColor}`}
                            style={{ maxHeight: "200px", maxWidth: "100%", objectFit: "contain", mixBlendMode: "multiply", filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.12))" }}
                          />
                          <span
                            style={{ fontFamily: "'Playfair Display', serif", fontSize: "14px", color: "#b8977e", letterSpacing: "0.02em" }}
                            data-testid="magnet-preview-label"
                          >
                            {magnetSize === "10x15" ? "10×15 cm" : "5×15 cm"} — {magnetColor === "blanc" ? "Blanc" : "Noir"}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.85rem 1.2rem", borderRadius: "1rem", cursor: "pointer", border: selectedOptions.backdrop ? "2px solid #b8977e" : "1.5px solid #f0e4da", background: selectedOptions.backdrop ? "#fff9f7" : "#fff", transition: "all 0.2s" }} data-testid="option-backdrop">
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <input type="checkbox" checked={selectedOptions.backdrop} onChange={(e) => setSelectedOptions(o => ({ ...o, backdrop: e.target.checked }))} style={{ width: 20, height: 20, accentColor: "#b8977e" }} />
                      <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a", fontSize: "0.95rem" }}>Fond pour photos</span>
                    </div>
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, color: "#b8977e" }}>+{optionsPrices.backdrop || 40}€</span>
                  </label>
                </div>
              </div>
            )}

            {selectedPackage && selectedPackage !== "contact" && pkgInfo && (
              <div style={{ maxWidth: 750, margin: "0 auto 2rem", padding: "0 1.5rem" }}>
                <div style={{ background: "#f5ede8", borderRadius: "1.5rem", padding: "1.5rem" }} data-testid="step1-summary">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a" }}>{pkgInfo.name} — {pkgInfo.duration}</span>
                  </div>
                  {Object.entries(selectedOptions).filter(([, v]) => v).map(([k]) => {
                    if (k === "magnet") {
                      const sizeLabel = magnetSize === "10x15" ? "10×15 cm" : "5×15 cm";
                      const colorLabel = magnetColor === "blanc" ? "Blanc" : "Noir";
                      const qtyLabel = `${magnetQty} unités`;
                      const magnetPrice = magnetQty === "100" ? 80 : 50;
                      return (
                        <div
                          key={k}
                          style={{
                            display: "flex", alignItems: "center", gap: "14px",
                            background: "#faf8f5", border: "1px solid #e8d5c4", borderRadius: "8px",
                            padding: "12px 16px", margin: "8px 0",
                            animation: "magnetRecapFade 200ms ease",
                          }}
                          data-testid="magnet-recap-badge"
                        >
                          <span style={{ fontSize: "18px", lineHeight: 1 }} aria-hidden>🧲</span>
                          <img
                            src={`/images/magnets/${magnetSize}_${magnetColor}.jpg`}
                            alt=""
                            style={{ width: 40, height: 40, objectFit: "contain", mixBlendMode: "multiply", flexShrink: 0 }}
                          />
                          <span
                            style={{ flex: 1, fontFamily: "'Montserrat', 'DM Sans', sans-serif", fontSize: "14px", color: "#5a5a5a" }}
                            data-testid="magnet-recap-text"
                          >
                            {sizeLabel} · {colorLabel} · {qtyLabel}
                            <button
                              type="button"
                              onClick={() => setShowMagnetInfo(true)}
                              data-testid="magnet-info-open-btn"
                              style={{
                                display: "block", marginTop: "2px", background: "none",
                                border: "none", padding: 0, color: "#b8977e",
                                fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: "12px",
                                textDecoration: "underline", cursor: "pointer",
                              }}
                            >
                              Voir un exemple →
                            </button>
                          </span>
                          <span
                            style={{ fontFamily: "'Playfair Display', serif", fontSize: "16px", color: "#b8977e", fontWeight: 600 }}
                            data-testid="magnet-recap-price"
                          >
                            +{magnetPrice}€
                          </span>
                        </div>
                      );
                    }
                    return (
                      <p key={k} style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#777" }}>+ {optLabels[k]}</p>
                    );
                  })}
                  <div style={{ borderTop: "1px solid #e8d5c4", margin: "0.75rem 0", paddingTop: "0.75rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, color: "#1a1a1a" }}>TOTAL</span>
                    <span style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "1.8rem", color: "#b8977e" }} data-testid="step1-total">{total.toFixed(2)}€</span>
                  </div>
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: "#999" }}>TVA non applicable - art. 293B CGI</p>
                </div>
              </div>
            )}

            <div style={{ maxWidth: 750, margin: "0 auto", padding: "0 1.5rem" }}>
              <button
                onClick={() => goStep(2)}
                disabled={!canStep2}
                style={{
                  width: "100%", padding: "1rem", border: "none", borderRadius: 50, fontFamily: "'DM Sans',sans-serif",
                  fontWeight: 600, fontSize: "1rem", color: "#fff", cursor: canStep2 ? "pointer" : "not-allowed",
                  background: canStep2 ? "linear-gradient(135deg, #b8977e, #c9a88a)" : "#ccc",
                  opacity: canStep2 ? 1 : 0.45,
                  boxShadow: canStep2 ? "0 4px 20px rgba(184,151,126,0.4)" : "none",
                  transition: "all 0.3s ease",
                }}
                data-testid="continue-step2-btn"
              >
                Continuer <ChevronRight style={{ display: "inline", width: 16, height: 16, verticalAlign: "middle" }} />
              </button>
            </div>
          </div>
        )}

        {/* ——— STEP 2 ——— */}
        {currentStep === 2 && (
          <div key={`s2-${animKey}`} style={slideStyle} className="py-8">
            <div style={{ maxWidth: 650, margin: "0 auto", padding: "0 1.5rem" }}>
              <div style={{ borderRadius: "2rem", padding: "2.5rem", boxShadow: "0 20px 60px rgba(0,0,0,0.08)", borderTop: "4px solid #b8977e", background: "#fff" }}>
                <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.8rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "1.5rem" }}>Vos informations</h2>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
                  <div>
                    <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.4rem" }}>Prénom *</label>
                    <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jean" style={inputStyle} className={inputFocusClass} data-testid="input-firstname" />
                  </div>
                  <div>
                    <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.4rem" }}>Nom *</label>
                    <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Dupont" style={inputStyle} className={inputFocusClass} data-testid="input-lastname" />
                  </div>
                </div>

                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.4rem" }}>Email *</label>
                  <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="jean@example.com" style={inputStyle} className={inputFocusClass} data-testid="input-email" />
                </div>

                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.4rem" }}>Téléphone *</label>
                  <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="+33 6 79 39 34 29" style={inputStyle} className={inputFocusClass} data-testid="input-phone" />
                </div>

                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.4rem" }}>Type d'événement</label>
                  <select value={eventType} onChange={(e) => setEventType(e.target.value)} style={{ ...inputStyle, appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 16px center" }} className={inputFocusClass} data-testid="input-event-type">
                    <option value="">-- Sélectionnez --</option>
                    <option value="Mariage">Mariage</option>
                    <option value="Anniversaire">Anniversaire</option>
                    <option value="Baptême">Baptême</option>
                    <option value="Corporate">Corporate</option>
                    <option value="Soirée privée">Soirée privée</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.4rem" }}>Lieu *</label>
                  <input value={eventLocation} onChange={(e) => setEventLocation(e.target.value)} placeholder="Paris, France" style={inputStyle} className={inputFocusClass} data-testid="input-location" />
                </div>

                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.4rem" }}>Zone de livraison *</label>
                  <select
                    value={deliveryZone}
                    onChange={(e) => setDeliveryZone(e.target.value)}
                    style={inputStyle}
                    className={inputFocusClass}
                    data-testid="select-delivery-zone"
                  >
                    <option value="idf">Île-de-France (livraison incluse)</option>
                    <option value="frontalier">Départements frontaliers IDF (+30€)</option>
                    <option value="hors_zone">Hors zone (non disponible)</option>
                  </select>
                  {deliveryZone === "frontalier" && (
                    <p data-testid="delivery-frontalier-hint" style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: "#b8977e", marginTop: "0.4rem" }}>
                      Aisne, Eure, Eure-et-Loir, Loiret, Marne, Oise, Yonne — supplément de 30€ appliqué.
                    </p>
                  )}
                  {deliveryZone === "hors_zone" && (
                    <div data-testid="delivery-hors-zone-warning" style={{ background: "#fff3f3", border: "1px solid #dc2626", borderRadius: 8, padding: "0.7rem 0.9rem", marginTop: "0.5rem", color: "#dc2626", fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem" }}>
                      ⚠️ Nous intervenons uniquement en Île-de-France et dans les départements frontaliers. Contactez-nous pour toute demande spécifique.
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.4rem" }}>Heure de début</label>
                  <input type="time" value={eventStartTime} onChange={(e) => setEventStartTime(e.target.value)} style={inputStyle} className={inputFocusClass} data-testid="input-time" />
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#555", display: "block", marginBottom: "0.4rem" }}>Message (optionnel)</label>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Informations supplémentaires..." style={{ ...inputStyle, resize: "none" }} className={inputFocusClass} data-testid="input-message" />
                </div>

                <div style={{ background: "#f5ede8", borderRadius: "1rem", padding: "1.2rem", marginBottom: "1.5rem" }}>
                  {pkgInfo && <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a", fontSize: "0.9rem" }}>{pkgInfo.name} — {pkgInfo.duration}</p>}
                  <p style={{ fontFamily: "'DM Sans',sans-serif", color: "#777", fontSize: "0.85rem" }}>{eventDate}{eventStartTime ? ` à ${eventStartTime}` : ""}</p>
                  {Object.entries(selectedOptions).filter(([, v]) => v).map(([k]) => (
                    <p key={k} style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: "#999" }}>+ {optLabels[k]}</p>
                  ))}
                  {deliveryZone === "frontalier" && (
                    <p data-testid="recap-frontalier-surcharge" style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: "#999" }}>+ Livraison frontaliers IDF (+30€)</p>
                  )}
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, color: "#b8977e", fontSize: "1.1rem", marginTop: "0.5rem" }}>Total : {total.toFixed(2)}€</p>
                </div>

                <div style={{ display: "flex", gap: "1rem" }}>
                  <button onClick={() => goStep(1)} style={{ border: "1.5px solid #b8977e", background: "transparent", color: "#b8977e", borderRadius: 50, padding: "0.9rem 2rem", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, cursor: "pointer" }} data-testid="back-step1-btn">
                    ← Retour
                  </button>
                  <button
                    onClick={() => goStep(3)}
                    disabled={!canStep3}
                    style={{
                      flexGrow: 1, border: "none", borderRadius: 50, padding: "0.9rem 2rem", fontFamily: "'DM Sans',sans-serif",
                      fontWeight: 600, color: "#fff", cursor: canStep3 ? "pointer" : "not-allowed",
                      background: canStep3 ? "linear-gradient(135deg, #b8977e, #c9a88a)" : "#ccc",
                      opacity: canStep3 ? 1 : 0.45,
                    }}
                    data-testid="continue-step3-btn"
                  >
                    Confirmer →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ——— STEP 3 — Signature du devis ——— */}
        {currentStep === 3 && (
          <div key={`s3-${animKey}`} style={slideStyle} className="py-8">
            <div style={{ maxWidth: 620, margin: "0 auto", padding: "0 1.5rem" }}>
              <div style={{ borderRadius: "1.5rem", padding: "2.5rem", boxShadow: "0 20px 60px rgba(0,0,0,0.08)", background: "#fff" }} data-testid="step3-signature">
                <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.6rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.25rem" }}>
                  Récapitulatif de votre devis
                </h2>
                <div style={{ width: 48, height: 2, background: "#b8977e", marginBottom: "1.5rem" }} />

                {/* Recap block */}
                <div style={{ background: "#faf6f2", border: "1px solid #f0e6dc", borderRadius: "1rem", padding: "1.5rem", marginBottom: "1.5rem" }} data-testid="step3-recap">
                  {pkgInfo && (
                    <>
                      <p style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "1.2rem", color: "#1a1a1a" }}>{pkgInfo.name} · {pkgInfo.duration}</p>
                      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem", color: "#777", marginBottom: "0.75rem" }}>{pkgInfo.description || "Impressions illimitées"}</p>
                    </>
                  )}
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem", color: "#444", marginBottom: "0.25rem" }}>📅 {eventDate}{eventStartTime ? ` · ${eventStartTime}` : ""}</p>
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem", color: "#444", marginBottom: "0.25rem" }}>📍 {eventLocation}</p>
                  {eventType && <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem", color: "#444", marginBottom: "0.75rem" }}>Type : {eventType}</p>}

                  <div style={{ borderTop: "1px solid #f0e6dc", margin: "0.75rem 0", paddingTop: "0.75rem" }}>
                    <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#999" }}>Client</p>
                    <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#1a1a1a" }}>{customerName}</p>
                    <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", color: "#666" }}>{customerEmail} · {customerPhone}</p>
                  </div>

                  {Object.entries(selectedOptions).filter(([, v]) => v).length > 0 && (
                    <div style={{ borderTop: "1px solid #f0e6dc", margin: "0.75rem 0", paddingTop: "0.75rem" }}>
                      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#999", marginBottom: "0.35rem" }}>Options sélectionnées</p>
                      {Object.entries(selectedOptions).filter(([, v]) => v).map(([k]) => (
                        <p key={k} style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#444" }}>+ {optLabels[k]}</p>
                      ))}
                    </div>
                  )}

                  <div style={{ borderTop: "1px solid #f0e6dc", marginTop: "0.75rem", paddingTop: "0.75rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, color: "#1a1a1a" }}>TOTAL</span>
                    <span style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "2rem", color: "#b8977e" }} data-testid="step3-total">{total.toFixed(2)}€</span>
                  </div>
                </div>

                {/* Payment modalities */}
                <div style={{ background: "#fff9f7", border: "1px solid #f0e6dc", borderRadius: "1rem", padding: "1.2rem 1.5rem", marginBottom: "1.5rem" }} data-testid="payment-modalities">
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <CreditCard style={{ width: 18, height: 18, color: "#b8977e" }} /> Modalités de paiement
                  </p>
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem", color: "#555", lineHeight: 1.5 }}>
                    Aucun paiement n&apos;est demandé maintenant. Le règlement s&apos;effectuera le jour de votre événement
                    <strong> (espèces, chèque ou virement)</strong>.
                  </p>
                </div>

                {/* Signature checkbox */}
                <label
                  htmlFor="quote-accept-checkbox"
                  style={{
                    display: "flex", alignItems: "flex-start", gap: "0.75rem",
                    padding: "1rem 1.2rem", borderRadius: "0.9rem",
                    border: quoteAccepted ? "2px solid #b8977e" : "1.5px solid #e8d5c4",
                    background: quoteAccepted ? "#fff9f7" : "#fff",
                    cursor: "pointer", marginBottom: "1rem", transition: "all .2s ease",
                  }}
                >
                  <input
                    id="quote-accept-checkbox"
                    type="checkbox"
                    checked={quoteAccepted}
                    onChange={(e) => setQuoteAccepted(e.target.checked)}
                    style={{ accentColor: "#b8977e", width: 20, height: 20, marginTop: 2, flexShrink: 0, cursor: "pointer" }}
                    data-testid="quote-accept-checkbox"
                  />
                  <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.92rem", color: "#1a1a1a", fontWeight: 600 }}>
                    J&apos;accepte le devis ci-dessus et confirme ma réservation
                  </span>
                </label>

                <button
                  onClick={handleSubmit}
                  disabled={loading || !quoteAccepted}
                  style={{
                    width: "100%", padding: "1.1rem", border: "none", borderRadius: 50, fontFamily: "'DM Sans',sans-serif",
                    fontWeight: 600, fontSize: "1rem", color: "#fff",
                    cursor: (loading || !quoteAccepted) ? "not-allowed" : "pointer",
                    background: "linear-gradient(135deg, #b8977e, #c9a88a)",
                    opacity: (loading || !quoteAccepted) ? 0.4 : 1,
                    boxShadow: (loading || !quoteAccepted) ? "none" : "0 4px 20px rgba(184,151,126,0.4)",
                    transition: "all 0.3s ease",
                  }}
                  data-testid="submit-booking-btn"
                >
                  {loading ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                      <Loader2 style={{ width: 18, height: 18, animation: "spin 1s linear infinite" }} /> Traitement...
                    </span>
                  ) : (
                    <span><Check style={{ display: "inline", width: 16, height: 16, verticalAlign: "middle", marginRight: 6 }} />Confirmer ma réservation</span>
                  )}
                </button>

                <p style={{ textAlign: "center", fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: "#999", marginTop: "0.75rem", lineHeight: 1.5 }}>
                  <Lock style={{ display: "inline", width: 12, height: 12, verticalAlign: "middle", marginRight: 4 }} />
                  En cochant cette case, vous vous engagez à honorer cette réservation.
                </p>

                <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
                  <button onClick={() => goStep(2)} style={{ border: "none", background: "none", color: "#b8977e", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, cursor: "pointer", fontSize: "0.9rem" }} data-testid="back-step2-btn">
                    ← Retour
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Mobile bottom bar: only when not embedded (avoid conflicts with LandingPage layout) */}
      {!embedded && pricingType !== null && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#f0e4da] px-4 py-3 flex items-center justify-between" data-testid="mobile-bottom-bar">
          <div>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: "#999" }}>Total</p>
            <p style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "1.3rem", color: "#b8977e" }}>{total.toFixed(2)}€</p>
          </div>
          {currentStep === 1 && (
            <button onClick={() => goStep(2)} disabled={!canStep2} style={{ padding: "0.7rem 1.5rem", borderRadius: 50, border: "none", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#fff", background: canStep2 ? "#b8977e" : "#ccc", opacity: canStep2 ? 1 : 0.45, cursor: canStep2 ? "pointer" : "not-allowed" }}>
              Continuer
            </button>
          )}
          {currentStep === 2 && (
            <button onClick={() => goStep(3)} disabled={!canStep3} style={{ padding: "0.7rem 1.5rem", borderRadius: 50, border: "none", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#fff", background: canStep3 ? "#b8977e" : "#ccc", opacity: canStep3 ? 1 : 0.45, cursor: canStep3 ? "pointer" : "not-allowed" }}>
              Confirmer
            </button>
          )}
          {currentStep === 3 && (
            <button onClick={handleSubmit} disabled={loading} style={{ padding: "0.7rem 1.5rem", borderRadius: 50, border: "none", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, color: "#fff", background: "#b8977e" }}>
              {loading ? "..." : "Payer"}
            </button>
          )}
        </div>
      )}

      {/* Magnet info modal */}
      {showMagnetInfo && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="magnet-info-title"
          onClick={() => setShowMagnetInfo(false)}
          data-testid="magnet-info-overlay"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px", animation: "magnetModalFade 250ms ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="magnet-info-modal"
            style={{
              position: "relative", background: "#ffffff", borderRadius: "16px",
              maxWidth: "480px", width: "100%", maxHeight: "92vh", overflow: "auto",
              boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
              animation: "magnetModalScale 250ms ease",
            }}
          >
            <div style={{ background: "#b8977e", padding: "22px 48px 22px 24px", borderRadius: "16px 16px 0 0", position: "relative" }}>
              <h3
                id="magnet-info-title"
                style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: "20px", color: "#ffffff", letterSpacing: "0.01em" }}
              >
                🧲 Photos Aimantées Souvenir
              </h3>
              <p style={{ margin: "6px 0 0 0", fontFamily: "'Playfair Display', serif", fontStyle: "italic", color: "#f5ede8", fontSize: "13px" }}>
                « Un souvenir que vos invités emportent chez eux »
              </p>
              <button
                type="button"
                onClick={() => setShowMagnetInfo(false)}
                aria-label="Fermer"
                data-testid="magnet-info-close-x"
                style={{
                  position: "absolute", top: "14px", right: "14px",
                  background: "rgba(255,255,255,0.18)", border: "none", color: "#ffffff",
                  width: 28, height: 28, borderRadius: "50%", cursor: "pointer",
                  fontSize: "16px", lineHeight: 1,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "22px 22px 6px 22px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                {[
                  { size: "10x15", color: "blanc", label: "10×15 cm · Blanc" },
                  { size: "10x15", color: "noir", label: "10×15 cm · Noir" },
                  { size: "5x15", color: "blanc", label: "5×15 cm · Blanc" },
                  { size: "5x15", color: "noir", label: "5×15 cm · Noir" },
                ].map((m) => (
                  <div
                    key={`${m.size}_${m.color}`}
                    style={{ background: "#faf8f5", border: "1px solid #e8d5c4", borderRadius: "10px", padding: "14px", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}
                  >
                    <img
                      src={`/images/magnets/${m.size}_${m.color}.jpg`}
                      alt={m.label}
                      style={{ width: "100%", maxHeight: "130px", objectFit: "contain", mixBlendMode: "multiply", filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.08))" }}
                    />
                    <span style={{ fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: "12px", color: "#b8977e", letterSpacing: "0.02em" }}>
                      {m.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <ul style={{ listStyle: "none", padding: "18px 26px 4px 26px", margin: 0 }}>
              {[
                "Impression HD instantanée",
                "Magnet inclus au dos",
                "Livré avec nœud décoratif",
              ].map((f) => (
                <li
                  key={f}
                  style={{ display: "flex", alignItems: "center", gap: "10px", fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: "13px", color: "#1a1a1a", padding: "4px 0" }}
                >
                  <span style={{ color: "#b8977e", fontWeight: 700 }}>✓</span> {f}
                </li>
              ))}
            </ul>

            <div style={{ padding: "16px 22px 22px 22px", textAlign: "right" }}>
              <button
                type="button"
                onClick={() => setShowMagnetInfo(false)}
                data-testid="magnet-info-close-btn"
                style={{
                  background: "transparent", color: "#b8977e", border: "1px solid #b8977e",
                  borderRadius: "999px", padding: "10px 28px",
                  fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: "13px",
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  cursor: "pointer", transition: "all 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#b8977e"; e.currentTarget.style.color = "#fff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#b8977e"; }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingWizard;
